import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

interface VideoPlayerProps {
  url: string;
  videoNumber?: number;
  title?: string;
  description?: string;
  imageUrls?: string[];
  onPlay?: () => void;
  iconOnly?: boolean;
}

/**
 * Extracts YouTube video ID from various YouTube URL formats
 */
const getYouTubeVideoId = (url: string): string | null => {
  const patterns = [
    /youtube\.com\/shorts\/([^&\n?#/]+)/,
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

/**
 * Extracts and converts YouTube timestamp parameter (t) to seconds
 * Supports formats: 295s, 295, 4m55s, 1h2m3s, etc.
 */
const getYouTubeStartTime = (url: string): number | null => {
  // Try to extract t parameter from URL
  const tMatch = url.match(/[?&]t=([^&\n?#]+)/i);
  if (!tMatch || !tMatch[1]) {
    return null;
  }

  const timeString = tMatch[1].toLowerCase();
  
  // If it's just a number (seconds), return it
  if (/^\d+$/.test(timeString)) {
    return parseInt(timeString, 10);
  }

  // If it ends with 's', remove it and parse as seconds
  if (timeString.endsWith('s') && /^\d+s?$/.test(timeString)) {
    return parseInt(timeString.replace('s', ''), 10);
  }

  // Parse complex formats like 4m55s, 1h2m3s
  let totalSeconds = 0;
  
  // Extract hours (h)
  const hoursMatch = timeString.match(/(\d+)h/);
  if (hoursMatch) {
    totalSeconds += parseInt(hoursMatch[1], 10) * 3600;
  }

  // Extract minutes (m)
  const minutesMatch = timeString.match(/(\d+)m/);
  if (minutesMatch) {
    totalSeconds += parseInt(minutesMatch[1], 10) * 60;
  }

  // Extract seconds (s) - but not if it's part of hours/minutes
  const secondsMatch = timeString.match(/(\d+)s(?!\d)/);
  if (secondsMatch) {
    totalSeconds += parseInt(secondsMatch[1], 10);
  }

  // If we found any time components, return the total
  if (hoursMatch || minutesMatch || secondsMatch) {
    return totalSeconds;
  }

  return null;
};

/**
 * Checks if URL is a YouTube link
 */
const isYouTubeUrl = (url: string): boolean => {
  return /youtube\.com|youtu\.be/.test(url);
};

/**
 * Checks if URL is a direct video file (mp4, webm, etc.)
 */
const isDirectVideoUrl = (url: string): boolean => {
  return /\.(mp4|webm|ogg|mov|avi|mkv)(\?.*)?$/i.test(url);
};

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, videoNumber, title, description, imageUrls = [], onPlay, iconOnly = false }) => {
  const [open, setOpen] = useState(false);
  const trimmedUrl = url.trim();

  const handlePlay = () => {
    setOpen(true);
    if (onPlay) {
      onPlay();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderVideoContent = () => {
    if (isYouTubeUrl(trimmedUrl)) {
      const videoId = getYouTubeVideoId(trimmedUrl);
      if (videoId) {
        // Extract start time from t parameter
        const startTime = getYouTubeStartTime(trimmedUrl);
        
        // Build embed URL with autoplay and optional start time
        let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
        if (startTime !== null && startTime > 0) {
          embedUrl += `&start=${startTime}`;
        }

        return (
          <Box
            sx={{
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              maxWidth: '100%',
            }}
          >
            <iframe
              width="100%"
              height="100%"
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        );
      }
    }

    if (isDirectVideoUrl(trimmedUrl)) {
      return (
        <Box
          component="video"
          controls
          autoPlay
          sx={{
            width: '100%',
            maxHeight: '80vh',
          }}
        >
          <source src={trimmedUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </Box>
      );
    }

    // Fallback: open in new tab
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          Video format not supported for inline playback.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <a href={trimmedUrl} target="_blank" rel="noopener noreferrer">
            Open video in new tab
          </a>
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <IconButton
        size="small"
        color="primary"
        onClick={handlePlay}
        aria-label={title || 'Play video'}
        sx={
          iconOnly
            ? {
                width: 36,
                height: 36,
                flexShrink: 0,
                bgcolor: '#e3f2fd',
                border: '1px solid #90caf9',
                color: '#1565c0',
                '&:hover': { bgcolor: '#bbdefb' },
              }
            : {
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                minWidth: 80,
              }
        }
      >
        <PlayArrowIcon fontSize={iconOnly ? 'small' : 'medium'} />
        {!iconOnly && title ? (
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            {title}
          </Typography>
        ) : !iconOnly && videoNumber !== undefined && (
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
            Video {videoNumber}
          </Typography>
        )}
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#000',
            width: '100%',
            maxWidth: '720px',
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'white',
            bgcolor: '#000',
          }}
        >
          <Typography variant="h6" component="span">
            {title || (videoNumber !== undefined ? `Video ${videoNumber}` : 'Video Player')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          {renderVideoContent()}
          {(description || imageUrls.length > 0) && (
            <Box sx={{ bgcolor: '#fff', color: '#1f2937', p: 2 }}>
              {description ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: imageUrls.length > 0 ? 1.5 : 0 }}>
                  {description}
                </Typography>
              ) : null}
              {imageUrls.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {imageUrls.map((imageUrl) => (
                    <Box
                      key={imageUrl}
                      component="img"
                      src={imageUrl}
                      alt={title || 'Video support image'}
                      sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #e5e7eb' }}
                    />
                  ))}
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoPlayer;

